import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Calendar, FileText, ListTodo, NotebookPen } from "lucide-react";
import { format } from "date-fns";
import TodoList from "@/components/TodoList";

/**
 * Per-client view inside the Therapist Portal.
 * Lets the therapist read sessions, edit the "About" page,
 * add private notes, and set to-dos for the client.
 */
export default function ClientDetail({ clientId, onBack }) {
  const [data, setData] = useState(null);
  const [about, setAbout] = useState("");
  const [savingAbout, setSavingAbout] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/therapist/me/clients/${clientId}`);
      setData(data);
      setAbout(data.about || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load client.");
      setData(false);
    }
  };

  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveAbout = async () => {
    setSavingAbout(true);
    try {
      await api.post("/therapist/me/client-about", { client_id: clientId, content: about });
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSavingAbout(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await api.post("/therapist/me/client-notes", { client_id: clientId, content: newNote });
      setNewNote("");
      toast.success("Note saved.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save note.");
    } finally {
      setAddingNote(false);
    }
  };

  if (data === null) return <div className="text-mindora-muted">loading…</div>;
  if (data === false) return <div className="text-mindora-ink">Could not load this client.</div>;

  const past = data.sessions.filter((s) => new Date(s.slot) <= new Date());
  const upcoming = data.sessions.filter((s) => new Date(s.slot) > new Date());
  const lastSession = past[0];

  return (
    <div data-testid="client-detail">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-mindora-muted hover:text-mindora-red mb-6"
        data-testid="client-detail-back"
      >
        <ArrowLeft size={14} /> all clients
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-4xl text-mindora-ink leading-tight">{data.name || "Client"}</h2>
          <div className="text-xs text-mindora-muted mt-1">{data.email}</div>
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-mindora-muted">
            <span>{data.sessions.length} {data.sessions.length === 1 ? "session" : "sessions"} total</span>
            {lastSession && <span>· last met {format(new Date(lastSession.slot), "PPP")}</span>}
            {upcoming[0] && <span>· next {format(new Date(upcoming[0].slot), "PPpp")}</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
        {/* About + last session */}
        <div className="lg:col-span-7 space-y-8">
          {/* About */}
          <section className="rounded-3xl border border-mindora-line bg-mindora-panel p-7 shadow-clay">
            <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em]">
              <FileText size={14} /> About this client
            </div>
            <p className="text-xs text-mindora-muted mt-1.5">Private to you. The client never sees this.</p>
            <Textarea
              rows={6}
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              data-testid="client-about-input"
              placeholder="Background, themes, what works in session, what to avoid, key relationships…"
              className="mt-4 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red font-serif text-base leading-relaxed"
            />
            <Button
              onClick={saveAbout}
              disabled={savingAbout}
              data-testid="client-about-save"
              className="mt-3 btn-primary rounded-full px-5 h-9 text-sm"
            >
              {savingAbout ? "Saving…" : "Save"}
            </Button>
          </section>

          {/* Last session highlight */}
          {lastSession && (
            <section className="rounded-3xl border border-mindora-line bg-mindora-bg p-7" data-testid="client-last-session">
              <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em]">
                <Calendar size={14} /> Last session
              </div>
              <div className="font-serif text-2xl text-mindora-ink mt-2">{format(new Date(lastSession.slot), "PPpp")}</div>
              {lastSession.note && <p className="mt-2 text-sm italic text-mindora-ink/80">Client&apos;s note: &ldquo;{lastSession.note}&rdquo;</p>}
            </section>
          )}

          {/* Notes */}
          <section className="rounded-3xl border border-mindora-line bg-mindora-panel p-7 shadow-clay">
            <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em]">
              <NotebookPen size={14} /> Session notes
            </div>
            <form onSubmit={addNote} className="mt-4">
              <Textarea
                rows={4}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                data-testid="client-note-input"
                placeholder="What happened in today's session? Themes, breakthroughs, follow-ups…"
                className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red font-serif leading-relaxed"
              />
              <Button type="submit" disabled={addingNote || !newNote.trim()} data-testid="client-note-add" className="mt-3 btn-primary rounded-full px-5 h-9 text-sm">
                {addingNote ? "Saving…" : "Add note"}
              </Button>
            </form>

            <div className="mt-6 space-y-3" data-testid="client-notes-list">
              {data.notes.length === 0 && <p className="text-sm italic text-mindora-muted">No notes yet.</p>}
              {data.notes.map((n) => (
                <div key={n.id} className="rounded-xl bg-mindora-bg border border-mindora-line px-4 py-3" data-testid={`client-note-${n.id}`}>
                  <div className="text-xs text-mindora-muted">{format(new Date(n.created_at), "PPpp")}</div>
                  <div className="mt-1.5 text-mindora-ink font-serif leading-relaxed whitespace-pre-wrap">{n.content}</div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Right column: todos + history */}
        <div className="lg:col-span-5 space-y-6">
          <div>
            <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em] mb-2">
              <ListTodo size={14} /> Set a to-do for this client
            </div>
            <TodoList clientId={clientId} therapistMode title="Send them a small action" />
            <p className="text-xs text-mindora-muted mt-2 italic">It appears on their dashboard immediately, gently tagged with your name.</p>
          </div>

          <section className="rounded-3xl border border-mindora-line bg-mindora-panel p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80 mb-3">Session history</div>
            <ul className="space-y-2.5" data-testid="client-session-history">
              {data.sessions.map((s) => (
                <li key={s.id} className="text-sm flex items-center justify-between gap-3 border-b border-mindora-line/60 last:border-0 pb-2 last:pb-0">
                  <span className="text-mindora-ink">{format(new Date(s.slot), "PPpp")}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-mindora-red">{s.status}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
